import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";
import { ConnectComponentsProvider, useCreateComponent } from "@stripe/react-connect-js";
import { Alert } from "@xola/ui-kit";
import { duration } from "common";
import React, { useCallback, useEffect, useMemo } from "react";
import { api } from "@/api/api";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { config } from "@/config";
import { useSeller } from "@/hooks/useAccount";

interface AccountSessionResponse {
    clientSecret: string;
}

const STORAGE_KEY = "stripe_connect_session";

const getStoredSession = (sellerId: ID): string | null => {
    try {
        const stored = sessionStorage.getItem(`${STORAGE_KEY}_${sellerId}`);
        if (!stored) return null;

        const { clientSecret, expiresAt } = JSON.parse(stored);
        if (Date.now() >= expiresAt) {
            sessionStorage.removeItem(`${STORAGE_KEY}_${sellerId}`);
            return null;
        }
        return clientSecret;
    } catch {
        return null;
    }
};

const setStoredSession = (sellerId: ID, clientSecret: string) => {
    try {
        const expiresAt = duration(55, "minutes"); // 55 minutes (sessions expire in 60)
        sessionStorage.setItem(`${STORAGE_KEY}_${sellerId}`, JSON.stringify({ clientSecret, expiresAt }));
    } catch {
        // Ignore storage errors
    }
};

const clearStoredSession = (sellerId: ID) => {
    try {
        sessionStorage.removeItem(`${STORAGE_KEY}_${sellerId}`);
    } catch {
        // Ignore storage errors
    }
};

const PaymentMethodSettingsComponent = React.memo(() => {
    const { wrapper } = useCreateComponent("payment-method-settings");
    return (
        <>
            <Alert color="primary">
                <span className="font-bold">Important:</span> Buy now, Pay Later payment methods incur a processing fee
                of 5.9% + 0.30 per transaction. Your standard credit card processing fees will not apply.
            </Alert>
            <div className="mt-6 min-h-[200px]">{wrapper}</div>
        </>
    );
});

let connectInstanceCache: StripeConnectInstance | null = null;
let currentSellerId: ID | null = null;
let pendingFetch: { sellerId: ID; promise: Promise<string> } | null = null;

export const StripePaymentMethodSettings = () => {
    const seller = useSeller();

    const fetchClientSecret = useCallback(async (): Promise<string> => {
        // Return existing in-flight promise for same seller (deduplication)
        if (pendingFetch?.sellerId === seller.id) {
            return pendingFetch.promise;
        }

        const cached = getStoredSession(seller.id);
        if (cached) {
            return cached;
        }

        const fetchPromise = api
            .post<AccountSessionResponse>(`/sellers/${seller.id}/stripeAccount/sessions`, {
                components: { payment_method_settings: { enabled: true } },
            })
            .then((response) => {
                const { clientSecret } = response.data;
                setStoredSession(seller.id, clientSecret);
                return clientSecret;
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                if (pendingFetch?.sellerId === seller.id) {
                    pendingFetch = null;
                }
            });

        pendingFetch = { sellerId: seller.id, promise: fetchPromise };
        return fetchPromise;
    }, [seller.id]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            clearStoredSession(seller.id);
        };

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === `${STORAGE_KEY}_${seller.id}` && !e.newValue && connectInstanceCache) {
                connectInstanceCache = null;
                currentSellerId = null;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("storage", handleStorageChange);
            // Clear caches on cleanup to avoid stale state on navigation
            if (pendingFetch?.sellerId === seller.id) {
                pendingFetch = null;
            }
            if (currentSellerId === seller.id) {
                connectInstanceCache = null;
                currentSellerId = null;
            }
        };
    }, [seller.id]);

    const connectInstance = useMemo(() => {
        if (currentSellerId !== seller.id) {
            connectInstanceCache = null;
            currentSellerId = seller.id;
        }

        if (connectInstanceCache) {
            return connectInstanceCache;
        }

        connectInstanceCache = loadConnectAndInitialize({
            publishableKey: seller.defaultGateway?.publicKey ?? config.stripe.publicKey,
            fetchClientSecret,
            appearance: {
                variables: {
                    colorPrimary: "#4F46E5",
                    colorBackground: "#ffffff",
                    fontFamily: "Inter, system-ui, sans-serif",
                    borderRadius: "8px",
                },
            },
        });

        return connectInstanceCache;
    }, [seller.id, seller.defaultGateway?.publicKey, fetchClientSecret]);

    return (
        <ContentLayout.Section
            title="Checkout Payment Methods"
            description="Configure additional payment methods available for your customers during checkout"
        >
            <ConnectComponentsProvider connectInstance={connectInstance}>
                <PaymentMethodSettingsComponent />
            </ConnectComponentsProvider>
        </ContentLayout.Section>
    );
};
